import { db } from '@/plugins/firebase'

export default {
  // Students
  getStudents: async ({ state, commit }, { sortBy, orientation }) => {
    const students = []
    let snapShot = null
    const studentsRef = db.collection('students').orderBy(sortBy, orientation)
    if (!state.lastStudent) {
      snapShot = await studentsRef.limit(state.pageBreak).get()
    } else {
      snapShot = await studentsRef.startAfter(state.lastStudent).limit(state.pageBreak).get()
    }

    if (!snapShot.empty) {
      commit('SET_LAST_STUDENT', snapShot.docs[snapShot.docs.length - 1])
    }
    snapShot.forEach(doc => {
      const student = doc.data()
      student.id = doc.id
      students.push(student)
    })
    commit('CONCAT_STUDENTS', students)
    commit('SET_EXIST_STUDENTS', !snapShot.empty)
  },

  getAllStudents: async ({ commit }, { sortBy, orientation }) => {
    const students = []
    const snapShot = await db.collection('students').orderBy(sortBy, orientation).get()
    snapShot.forEach(doc => {
      const student = doc.data()
      student.id = doc.id
      students.push(student)
    })
    commit('SET_STUDENTS', students)
  },

  resetStudents: async ({ commit }) => {
    commit('SET_LAST_STUDENT', null)
    commit('SET_EXIST_STUDENTS', false)
    commit('SET_STUDENTS', [])
  },
  setSortBy: async ({ commit }, payload) => {
    commit('SET_SORT_BY', payload)
  },
  setOrientation: async ({ commit }, payload) => {
    commit('SET_ORIENTATION', payload)
  },
  setRequestAllStudents: async ({ commit }, payload) => {
    commit('SET_REQUEST_ALL_STUDENTS', payload)
  },
  setSearchValue: async ({ commit }, payload) => {
    commit('SET_SEARCH_VALUE', payload)
  },

  deleteStudent: async ({ commit }, studentId) => {
    try {
      const { functions } = await import('@/plugins/firebase')
      const deleteStudentsCallable = functions.httpsCallable('deleteStudentsCallable')
      const response = await deleteStudentsCallable({ studentIds: [studentId] })
      const data = response?.data || {}
      const deleted = data?.results?.deleted || []
      const errors = data?.results?.errors || []

      if (deleted.includes(studentId)) {
        commit('REMOVE_STUDENT', studentId)
        return { success: true, deleted, errors }
      }

      const errorEntry = errors.find((entry) => entry.studentId === studentId)
      const message = errorEntry?.error || 'Failed to delete student'
      throw new Error(message)
    } catch (error) {
      console.error('Error deleting student:', error)
      throw error
    }
  },

  deleteStudents: async ({ commit }, studentIds = []) => {
    if (!Array.isArray(studentIds) || studentIds.length === 0) {
      return { success: true, deleted: [], errors: [] }
    }

    try {
      const { functions } = await import('@/plugins/firebase')
      const deleteStudentsCallable = functions.httpsCallable('deleteStudentsCallable')
      const response = await deleteStudentsCallable({ studentIds })
      const data = response?.data || {}
      const deleted = data?.results?.deleted || []
      const errors = data?.results?.errors || []

      deleted.forEach((id) => commit('REMOVE_STUDENT', id))

      return {
        success: data?.success ?? errors.length === 0,
        deleted,
        errors
      }
    } catch (error) {
      console.error('Error deleting students:', error)
      throw error
    }
  },

  setEditedStudent: async ({ commit }, student) => {
    commit('SET_EDITED_STUDENT', student)
  },

  addStudent: async ({ commit }, payload) => {
    const studentData = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      flashcardsEnabled: payload.flashcardsEnabled !== undefined ? payload.flashcardsEnabled : true,
      verified: false,
      createdAt: new Date()
    }

    const docRef = await db.collection('students').add(studentData)
    studentData.id = docRef.id

    commit('ADD_STUDENT', studentData)
    return { success: true, id: docRef.id }
  },

  editStudent: async ({ commit }, payload) => {
    const docRef = db.collection('students').doc(payload.id)
    await docRef.update({
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      flashcardsEnabled: payload.flashcardsEnabled !== undefined ? payload.flashcardsEnabled : true
    })

    // Obtener los datos actualizados
    const updatedStudent = (await docRef.get()).data()
    updatedStudent.id = payload.id

    return { success: true }
  },

  enableStudentFlashcards: async ({ commit }, studentId) => {
    // Verificar que el studentId sea válido
    if (!studentId) {
      console.error('ERROR: studentId es inválido:', studentId)
      throw new Error('ID de estudiante inválido')
    }

    try {
      console.log('Iniciando proceso de habilitación de flashcards para estudiante ID:', studentId)

      // Importar Firebase de manera dinámica
      const { functions, auth } = await import('@/plugins/firebase')

      // Obtener el token de autenticación actual
      const currentUser = auth.currentUser
      if (!currentUser) {
        console.error('Error: Usuario no autenticado al intentar habilitar flashcards')
        throw new Error('Usuario no autenticado')
      }

      // Esto evita problemas de CORS ya que las funciones callable manejan CORS automáticamente
      console.log('Usando función callable enableStudentCallable')

      // Preparar datos para enviar
      const payload = { studentId }
      console.log('Llamando a enableStudentCallable con payload:', payload)

      try {
        // Crear referencia a la función callable
        const enableStudentCallable = functions.httpsCallable('enableStudentCallable')

        // Realizar la llamada a la función callable
        const result = await enableStudentCallable(payload)
        console.log('Respuesta de enableStudentCallable:', result.data)

        // Verificar respuesta
        if (!result.data || !result.data.success) {
          throw new Error(result.data?.error || 'Error desconocido del servidor')
        }

        // Actualizar también en Firestore directamente para asegurar consistencia
        try {
          await db.collection('students').doc(studentId).update({
            flashcardsEnabled: true
          })
          console.log(`Actualizado flashcardsEnabled=true en Firestore para estudiante ${studentId}`)
        } catch (firestoreError) {
          console.error('Error al actualizar flashcardsEnabled en Firestore:', firestoreError)
          // Continuamos aunque falle la actualización en Firestore, ya que la Cloud Function también lo actualiza
        }

        // Actualizar estado local
        commit('UPDATE_STUDENT_FLASHCARDS', { id: studentId, flashcardsEnabled: true })
        return { success: true, result: result.data }
      } catch (error) {
        console.error('Error detallado al habilitar flashcards:', {
          message: error.message,
          code: error.code,
          details: error.details,
          functionName: 'enableStudentCallable'
        })
        throw error
      }
    } catch (error) {
      console.error('Error general en enableStudentFlashcards:', error)
      throw error
    }
  }
}
